import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Telescope, ImageOff, BookText, Beaker, Leaf, Sparkles, Play, X as XIcon, Loader, ShieldCheck, RotateCcw } from "lucide-react";
import { enrichQueue, enrichStartJob, enrichListJobs, enrichCancelJob, enrichSeedSources } from "../../lib/adminApi";
import { adminApi } from "../../lib/adminApi";

const FILTERS = [
  { key: "", label: "All gins", icon: Sparkles },
  { key: "missing_image", label: "Missing image", icon: ImageOff },
  { key: "missing_story", label: "Missing story", icon: BookText },
  { key: "missing_botanicals", label: "Missing botanicals", icon: Leaf },
  { key: "missing_flavour", label: "Missing flavour", icon: Beaker },
  { key: "has_pending", label: "Pending review", icon: Telescope },
];

export default function AdminEnrichmentQueue() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [jobs, setJobs] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkScope, setBulkScope] = useState("missing_flavour_map");
  const [includeFlavour, setIncludeFlavour] = useState(true);
  const [includeText, setIncludeText] = useState(false);

  const refresh = () => {
    enrichQueue(filter || null).then(setData);
    enrichListJobs().then((d) => setJobs(d.jobs || []));
  };
  useEffect(() => { refresh(); }, [filter]);

  // Poll the running job every 3s
  useEffect(() => {
    const running = jobs.find((j) => j.status === "running");
    if (!running) return;
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs.map((j) => j.status).join(",")]);

  const startJob = async () => {
    setBulkBusy(true);
    try {
      const r = await enrichStartJob({
        scope: bulkScope,
        include_flavour_map: includeFlavour,
        include_text_enrichment: includeText,
        only_available: true,
      });
      alert(`Job ${r.job_id.slice(0, 8)}... queued (${r.queued_skus} gins).`);
      setShowBulk(false);
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not start job");
    } finally { setBulkBusy(false); }
  };

  const cancelJob = async (id) => {
    if (!confirm("Cancel this job?")) return;
    const r = await enrichCancelJob(id);
    if (r.status === "completed") {
      alert("Job already completed before cancel could land.");
    }
    refresh();
  };

  if (!data) return <div className="p-10 text-white/40">Loading the queue...</div>;
  const running = jobs.find((j) => j.status === "running");

  return (
    <div className="px-5 md:px-10 py-8 max-w-7xl" data-testid="admin-enrichment-queue">
      <header className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="overline text-[#C17F59] mb-2 flex items-center gap-2"><Telescope size={12} /> Enrichment Queue</p>
          <h1 className="font-display text-4xl">The research room.</h1>
          <p className="font-hand text-xl text-[#D89A74] -rotate-1 mt-1">— every gin, every gap, in one view.</p>
        </div>
        <button onClick={() => setShowBulk((s) => !s)}
                className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74] shrink-0"
                data-testid="bulk-toggle">
          <Play size={14} /> Run Scout for all SKUs
        </button>
      </header>

      {/* Bulk job configurator */}
      {showBulk && (
        <section className="bg-[#101113] border border-[#C17F59]/30 p-5 mb-6" data-testid="bulk-config">
          <p className="overline text-[#C17F59] mb-3 flex items-center gap-2"><Play size={11} /> Bulk Enrichment</p>
          <p className="text-sm text-white/55 mb-4">
            Scope determines which gins enter the queue. Nothing publishes — every result lands in
            the per-gin review queue. Wikipedia fallback always confidence = PARTIAL.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <label className="block">
              <span className="overline text-white/50 mb-1 block">Scope</span>
              <select value={bulkScope} onChange={(e) => setBulkScope(e.target.value)}
                      className="w-full bg-transparent border-b border-white/15 focus:border-[#C17F59] py-2 text-sm focus:outline-none"
                      data-testid="bulk-scope">
                <option value="all">All available gins</option>
                <option value="missing_image">Only missing image</option>
                <option value="missing_flavour_map">Only missing flavour map</option>
                <option value="missing_story">Only missing story</option>
              </select>
            </label>
            <div className="flex flex-col gap-2 justify-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={includeFlavour} onChange={(e) => setIncludeFlavour(e.target.checked)}
                       className="accent-[#C17F59]" data-testid="bulk-include-flavour" />
                <span className="text-xs text-white/70">Include flavour map (DNA suggestions)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={includeText} onChange={(e) => setIncludeText(e.target.checked)}
                       className="accent-[#C17F59]" data-testid="bulk-include-text" />
                <span className="text-xs text-white/70">Include text enrichment (story, production, awards…)</span>
              </label>
            </div>
          </div>
          <button onClick={startJob} disabled={bulkBusy || !!running}
                  className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-2.5 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74] disabled:opacity-50"
                  data-testid="bulk-start">
            <Play size={12} /> {bulkBusy ? "Queueing..." : running ? "A job is running" : "Queue job"}
          </button>
          {running && <span className="ml-3 text-xs text-amber-400">Another job is running — cancel it first.</span>}
        </section>
      )}

      {/* Running job progress */}
      {running && (
        <section className="bg-[#15171A] border border-[#C17F59]/30 p-5 mb-6" data-testid="running-job">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <p className="overline text-[#C17F59] flex items-center gap-2">
              <Loader size={12} className="animate-spin" /> Bulk job running
            </p>
            <button onClick={() => cancelJob(running.id)}
                    className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-red-400 border border-white/15 hover:border-red-400/40 px-3 py-1.5 uppercase tracking-[0.22em]"
                    data-testid="job-cancel">
              <XIcon size={11} /> Cancel
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <Stat label="Processed" value={`${running.processed_skus} / ${running.total_skus}`} />
            <Stat label="Success" value={running.success_count} tone="green" />
            <Stat label="Failed" value={running.failed_count} tone="red" />
            <Stat label="Skipped" value={running.skipped_count || 0} tone="amber" />
            <Stat label="Last" value={running.last_processed_gin_id || "—"} />
          </div>
          <div className="h-1 bg-white/8 overflow-hidden">
            <div className="h-full bg-[#C17F59] transition-all duration-500"
                 style={{ width: `${Math.round((running.processed_skus / running.total_skus) * 100)}%` }} />
          </div>
        </section>
      )}

      {/* Recent jobs */}
      {jobs.length > 0 && (
        <section className="mb-6" data-testid="jobs-history">
          <p className="overline text-white/40 mb-2">Recent jobs</p>
          <div className="space-y-1">
            {jobs.slice(0, 5).map((j) => (
              <div key={j.id} className="bg-[#15171A] border border-white/8 p-3 flex items-center justify-between text-xs text-white/70 gap-3 flex-wrap" data-testid={`job-${j.id}`}>
                <span className="font-mono text-[10px] text-white/40">{j.id.slice(0, 8)}</span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#C17F59]">{j.scope}</span>
                <span className={`text-[10px] uppercase tracking-[0.22em] ${
                  j.status === "completed" ? "text-green-400" :
                  j.status === "running" ? "text-[#D89A74]" :
                  j.status === "cancelled" ? "text-amber-400" : "text-white/50"
                }`}>{j.status}</span>
                <span>{j.processed_skus}/{j.total_skus}</span>
                <span className="text-green-400">{j.success_count}✓</span>
                <span className="text-red-400">{j.failed_count}✗</span>
                <span className="text-amber-400">{j.skipped_count || 0}⊘</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* BULK DNA MODERATION */}
      <DnaModerationPanel />

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { k: "missing_image", l: "No bottle image" },
          { k: "missing_story", l: "No story" },
          { k: "missing_botanicals", l: "No botanicals" },
          { k: "missing_flavour", l: "No flavour notes" },
          { k: "with_pending", l: "Pending review" },
        ].map((t) => (
          <div key={t.k} className="bg-[#15171A] border border-white/8 p-4">
            <p className="font-display text-4xl text-[#C17F59] tabular-nums leading-none">{data.totals[t.k] ?? 0}</p>
            <p className="overline text-white/50 mt-2">{t.l}</p>
          </div>
        ))}
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-1 mb-5 border-b border-white/8 overflow-x-auto no-scrollbar" data-testid="queue-filters">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button key={f.key || "all"} onClick={() => setFilter(f.key)}
                    className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] px-3 py-2 transition-colors shrink-0 ${
                      active ? "text-[#C17F59] border-b border-[#C17F59]" : "text-white/50 hover:text-white"
                    }`}
                    data-testid={`queue-filter-${f.key || "all"}`}>
              <f.icon size={11} /> {f.label}
            </button>
          );
        })}
      </div>

      {/* Queue table */}
      <div className="bg-[#15171A] border border-white/8 overflow-x-auto">
        <table className="w-full text-sm" data-testid="queue-table">
          <thead>
            <tr className="border-b border-white/8 text-left">
              <th className="py-3 px-4 overline text-white/40">Gin</th>
              <th className="py-3 px-4 overline text-white/40">Country</th>
              <th className="py-3 px-4 overline text-white/40">Coverage</th>
              <th className="py-3 px-4 overline text-white/40 text-right">Pending</th>
              <th className="py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {data.queue.map((r) => {
              const pct = Math.round(r.completeness * 100);
              const totalPending = r.pending_suggestions + r.pending_image_candidates;
              return (
                <tr key={r.gin_id} className="border-b border-white/4 hover:bg-white/3 transition-colors" data-testid={`queue-row-${r.gin_id}`}>
                  <td className="py-3 px-4">
                    <p className="font-display text-base">{r.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{r.gin_id}</p>
                  </td>
                  <td className="py-3 px-4 text-white/70">{r.country}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="h-[3px] w-24 bg-white/8 overflow-hidden">
                        <div className="h-full bg-[#C17F59]" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-white/60 tabular-nums">{pct}%</span>
                    </div>
                    <p className="text-[9px] uppercase tracking-[0.2em] text-white/40 mt-1">
                      {Object.entries(r.coverage).filter(([, v]) => !v).map(([k]) => k).join(" · ") || "complete"}
                    </p>
                  </td>
                  <td className="py-3 px-4 text-right">
                    {totalPending > 0 ? (
                      <span className="inline-block text-[10px] uppercase tracking-[0.22em] text-[#D89A74] border border-[#C17F59]/40 px-2 py-1" data-testid={`queue-pending-${r.gin_id}`}>
                        {totalPending} pending
                      </span>
                    ) : <span className="text-white/30 text-xs">—</span>}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Link to={`/admin/menu/${r.gin_id}/enrich`}
                          className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.22em] text-[#C17F59] hover:text-[#D89A74]"
                          data-testid={`queue-open-${r.gin_id}`}>
                      Open →
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data.queue.length === 0 && <p className="text-white/40 text-sm p-6 text-center">Nothing in this view.</p>}
      </div>
    </div>
  );
}

function DnaModerationPanel() {
  const [expanded, setExpanded] = useState(false);
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({
    only_confidence_states: ["VERIFIED", "STRONG"],
    only_source_types: ["official_brand", "distillery", "competition", "editorial"],
    require_whitelisted_source: true,
    exclude_wikipedia_fallback: true,
    exclude_retailers: true,
    allow_overwrite_manual_dna: false,
  });

  const fetchPreview = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/enrichment/dna-moderation/preview", filters);
      setPreview(r.data);
    } catch (e) { alert(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview || preview.ready_count === 0) return;
    if (!confirm(`Approve ${preview.ready_count} trusted DNA suggestions? Previous DNA will be snapshotted for rollback.`)) return;
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/enrichment/dna-moderation/apply",
        { filters, dry_run: false });
      alert(`Applied ${r.data.applied} DNA updates. Bulk id: ${r.data.bulk_id.slice(0, 8)}...`);
      setPreview(null); loadHistory();
    } catch (e) { alert(e.response?.data?.detail || "Apply failed"); }
    finally { setBusy(false); }
  };

  const loadHistory = async () => {
    try {
      const r = await adminApi.get("/admin/enrichment/dna-moderation/history");
      setHistory(r.data.history || []);
    } catch (e) { /* ignore */ }
  };

  const rollback = async (id) => {
    if (!confirm("Roll back this bulk job? Every gin's DNA will be restored from the snapshot.")) return;
    try {
      const r = await adminApi.post(`/admin/enrichment/dna-moderation/${id}/rollback`);
      alert(`Restored ${r.data.restored} gins.`);
      loadHistory();
    } catch (e) { alert(e.response?.data?.detail || "Rollback failed"); }
  };

  useEffect(() => { if (expanded) loadHistory(); }, [expanded]);

  return (
    <section className="bg-[#15171A] border border-white/8 p-5 mb-6" data-testid="dna-moderation-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap cursor-pointer" onClick={() => setExpanded((e) => !e)}>
        <p className="overline text-[#C17F59] flex items-center gap-2">
          <ShieldCheck size={12} /> Bulk DNA Moderation
        </p>
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
          {expanded ? "Collapse" : "Expand"}
        </span>
      </div>

      {expanded && (
        <div className="mt-4 space-y-4">
          <p className="text-sm text-white/55">
            Approve only trusted, high-confidence flavour-map suggestions in one pass. Manual DNA is
            always protected. Wikipedia and retailers are excluded by default. Every change is
            snapshotted for rollback.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={filters.exclude_wikipedia_fallback}
                     onChange={(e) => setFilters({ ...filters, exclude_wikipedia_fallback: e.target.checked })}
                     className="accent-[#C17F59]" data-testid="dna-exclude-wiki" />
              <span className="text-white/70">Exclude Wikipedia fallback</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={filters.exclude_retailers}
                     onChange={(e) => setFilters({ ...filters, exclude_retailers: e.target.checked })}
                     className="accent-[#C17F59]" data-testid="dna-exclude-retailer" />
              <span className="text-white/70">Exclude retailers</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={filters.require_whitelisted_source}
                     onChange={(e) => setFilters({ ...filters, require_whitelisted_source: e.target.checked })}
                     className="accent-[#C17F59]" data-testid="dna-require-whitelisted" />
              <span className="text-white/70">Require whitelisted source domain</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={filters.allow_overwrite_manual_dna}
                     onChange={(e) => setFilters({ ...filters, allow_overwrite_manual_dna: e.target.checked })}
                     className="accent-[#C17F59]" data-testid="dna-allow-manual-overwrite" />
              <span className="text-red-400/80">Allow overwrite of manual admin DNA (dangerous)</span>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={fetchPreview} disabled={busy}
                    className="inline-flex items-center gap-2 border border-[#C17F59]/40 text-[#C17F59] hover:bg-[#C17F59] hover:text-[#0E0F0C] px-4 py-2 text-xs uppercase tracking-[0.22em] disabled:opacity-50"
                    data-testid="dna-preview">
              {busy ? "Computing..." : "Preview impact"}
            </button>
            {preview && (
              <button onClick={apply} disabled={preview.ready_count === 0 || busy}
                      className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-4 py-2 text-xs uppercase tracking-[0.22em] hover:bg-[#D89A74] disabled:opacity-50"
                      data-testid="dna-apply">
                <ShieldCheck size={11} /> Approve {preview.ready_count} trusted suggestions
              </button>
            )}
          </div>

          {/* Preview output */}
          {preview && (
            <div className="bg-[#0E0F0C] border border-[#C17F59]/30 p-4" data-testid="dna-preview-result">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <p className="font-display text-3xl text-[#C17F59] tabular-nums leading-none">{preview.ready_count}</p>
                <p className="overline text-white/50">ready to apply</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[10px] text-white/55 mb-4">
                <span>{preview.skipped.manual_dna_protected} manual DNA protected</span>
                <span>{preview.skipped.wikipedia_fallback} Wikipedia fallback</span>
                <span>{preview.skipped.confidence_too_low} confidence too low</span>
                <span>{preview.skipped.source_type_excluded} source type excluded</span>
                <span>{preview.skipped.retailer_excluded} retailer excluded</span>
              </div>
              {/* Axes that will change */}
              <p className="overline text-white/40 mb-2">Axes affected (count of gins changing on each axis)</p>
              <div className="grid grid-cols-5 md:grid-cols-10 gap-2 text-center">
                {Object.entries(preview.axes_changed).map(([a, c]) => (
                  <div key={a} className="bg-[#15171A] border border-white/8 p-2" data-testid={`dna-axis-${a}`}>
                    <p className="overline text-white/40 capitalize">{a}</p>
                    <p className={`font-display text-2xl tabular-nums leading-none ${c > 0 ? "text-[#C17F59]" : "text-white/20"}`}>{c}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* History + rollback */}
          {history.length > 0 && (
            <div className="border-t border-white/5 pt-4">
              <p className="overline text-white/40 mb-2">Recent bulk DNA jobs</p>
              <div className="space-y-1">
                {history.slice(0, 5).map((h) => (
                  <div key={h.id} className="bg-[#0E0F0C] border border-white/8 p-3 flex items-center justify-between text-xs text-white/60 gap-3 flex-wrap" data-testid={`dna-history-${h.id}`}>
                    <span className="font-mono text-[10px] text-white/40">{h.id.slice(0, 8)}</span>
                    <span>{h.applied_count} applied</span>
                    <span className="text-white/40">{new Date(h.approved_at).toLocaleString()}</span>
                    {h.rolled_back_at ? (
                      <span className="text-amber-400 text-[10px] uppercase tracking-[0.22em]">rolled back</span>
                    ) : (
                      <button onClick={() => rollback(h.id)}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] border border-white/15 hover:border-amber-400/40 hover:text-amber-400 px-2 py-1"
                              data-testid={`dna-rollback-${h.id}`}>
                        <RotateCcw size={10} /> Roll back
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, tone }) {  const cls = tone === "green" ? "text-green-400" :
              tone === "red" ? "text-red-400" :
              tone === "amber" ? "text-amber-400" : "text-[#C17F59]";
  return (
    <div className="bg-[#0E0F0C] border border-white/8 p-3">
      <p className={`font-display text-3xl tabular-nums leading-none ${cls}`}>{value}</p>
      <p className="overline text-white/50 mt-2">{label}</p>
    </div>
  );
}
